// Single TypeScript home for the enums that Guide §4–§6 declares in Prisma. Keep in sync with packages/db/prisma/schema.
import { z } from 'zod';

export const PLANS = ['free', 'basic', 'pro', 'business', 'enterprise'] as const;
export type Plan = (typeof PLANS)[number];
export type PaidPlan = Exclude<Plan, 'free' | 'enterprise'>;
export const PAID_PLANS = ['basic', 'pro', 'business'] as const satisfies readonly PaidPlan[];

export const SUB_STATUSES = ['trial', 'active', 'past_due', 'free'] as const;
export type SubStatus = (typeof SUB_STATUSES)[number];

export const FREE_REASONS = ['trial_ended', 'lapsed', 'cancelled', 'admin'] as const;
export type FreeReason = (typeof FREE_REASONS)[number];

export const GRANT_TYPES = ['complimentary', 'correction', 'partner'] as const;
export type GrantType = (typeof GRANT_TYPES)[number];

export const SHOP_KINDS = ['business', 'personal'] as const;
export type ShopKind = (typeof SHOP_KINDS)[number];

export const CATEGORIES = [
  'clothing',
  'departmental',
  'electronics',
  'general',
  'grocery',
  'hardware',
  'mobile_banking',
  'personal_savings',
  'pharmacy',
  'restaurant',
  'real_estate',
  'software',
  'agriculture',
  'wholesale',
  'tailoring',
  'stationary',
  'others',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const STAFF_ROLES = ['manager', 'cashier'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];
export type Role = 'owner' | StaffRole;
export const ROLES = ['owner', 'manager', 'cashier'] as const;

export const STAFF_STATUSES = ['invited', 'active', 'deactivated', 'plan_suspended'] as const;
export type StaffStatus = (typeof STAFF_STATUSES)[number];

export const PRINCIPAL_TYPES = ['owner', 'staff'] as const;
export type PrincipalType = (typeof PRINCIPAL_TYPES)[number];

export const LANGS = ['bn', 'en'] as const;
export type Lang = (typeof LANGS)[number];

/** v10 adds pharmacy, clothing, grocery, hardware and stationery units (labels, whole/fraction rule and per-category lists: ./units.ts). Append only: the DB enum "Unit" mirrors this list. */
export const UNITS = [
  'pcs', 'kg', 'g', 'l', 'ml', 'dozen', 'box', 'packet', 'ft', 'm', 'other',
  'strip', 'tablet', 'capsule', 'bottle', 'vial', 'ampoule', 'tube', 'sachet', 'pair', 'set', 'yard', 'inch', 'hali', 'maund', 'carton', 'bag', 'can', 'jar', 'roll', 'sheet', 'ream', 'sqft', 'cft', 'ton',
] as const;
export type Unit = (typeof UNITS)[number];

/** Guide §5 + D17: payment methods incl. rocket/upay/card/bank/other */
export const PAYMENT_METHODS = ['cash', 'bkash', 'nagad', 'rocket', 'upay', 'card', 'bank', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** dps / fdr / wallet are Personal Savings places (Dev Guide §7.2) */
export const MONEY_ACCOUNT_TYPES = ['cash', 'bkash', 'nagad', 'rocket', 'upay', 'bank', 'other', 'dps', 'fdr', 'wallet'] as const;
export type MoneyAccountType = (typeof MONEY_ACCOUNT_TYPES)[number];

/**
 * Money movements that make up the Home "cash in today" figure: what customers paid, minus what was handed back.
 * v10: refunds and voids count too, so a return or a voided bill takes its money out of today's cash in.
 */
export const CASH_IN_REASONS = ['sale_payment', 'baki_collection', 'refund', 'refund_void', 'sale_void', 'sale_restore'] as const;
/** DPS and FDR are savings instruments: money is paid into them, never spent from them. */
export const SAVINGS_INSTRUMENT_TYPES = ['dps', 'fdr'] as const;
export const isSavingsInstrument = (t: string | null | undefined): boolean => t === 'dps' || t === 'fdr';

export const STOCK_REASONS = [
  'opening',
  'purchase',
  'sale',
  'sale_return',
  'void_reversal',
  'adjustment_damage',
  'adjustment_expired',
  'adjustment_count',
  'vendor_return',
] as const;
export type StockReason = (typeof STOCK_REASONS)[number];

export const LEDGER_TYPES = ['sale_credit', 'payment', 'return_credit', 'writeoff', 'opening', 'adjustment', 'advance'] as const;
export type LedgerType = (typeof LEDGER_TYPES)[number];

export const SALE_STATUSES = ['completed', 'voided'] as const;
export type SaleStatus = (typeof SALE_STATUSES)[number];

export const PAYMENT_STATUSES = [
  'initiated',
  'pending',
  'succeeded',
  'failed',
  'cancelled',
  'refunded',
  'partially_refunded',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_PURPOSES = ['subscription', 'upgrade', 'extra_shop', 'sms_package', 'personal_subscription', 'ai_credits'] as const;
export type PaymentPurpose = (typeof PAYMENT_PURPOSES)[number];

/** Personal Savings is its own paid product (Bible 6.8.1, DL-22): its own state, separate from the Business plan. */
export const PERSONAL_STATUSES = ['none', 'trial', 'active', 'past_due', 'restricted'] as const;
export type PersonalStatus = (typeof PERSONAL_STATUSES)[number];
export const PERSONAL_CYCLES = ['monthly', 'yearly'] as const;
export type PersonalCycle = (typeof PERSONAL_CYCLES)[number];

/** How a customer pays: scan the Bangla QR and wait for a person to verify, or the bKash gateway (verified automatically). */
export const CHECKOUT_METHODS = ['bangla_qr', 'bkash'] as const;
export type CheckoutMethod = (typeof CHECKOUT_METHODS)[number];

export const SMS_PACKAGE_KEYS = ['starter', 'standard', 'business'] as const;
export type SmsPackageKey = (typeof SMS_PACKAGE_KEYS)[number];

export const ADMIN_ROLES = ['super_admin', 'support', 'finance', 'content', 'custom'] as const;
/** roles the Super Admin can give to a team member (there is exactly one Super Admin) */
export const TEAM_ROLES = ['support', 'finance', 'content', 'custom'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const RETURN_REASONS = ['defect', 'damaged', 'expired', 'preference', 'other'] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];

export const TICKET_CATEGORIES = ['recovery', 'billing', 'bug', 'how_to', 'feature', 'data_privacy'] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];
export const TICKET_PRIORITIES = ['P1', 'P2', 'P3'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];
export const TICKET_STATUSES = ['open', 'pending_customer', 'pending_internal', 'resolved', 'closed'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const zPlan = z.enum(PLANS);
export const zCategory = z.enum(CATEGORIES);
export const zUnit = z.enum(UNITS);
export const zPaymentMethod = z.enum(PAYMENT_METHODS);
export const zLang = z.enum(LANGS);
export const zRole = z.enum(ROLES);
export const zStaffRole = z.enum(STAFF_ROLES);
